import { isDefined } from "@clipboard-health/util-ts";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { type EmergencyCreditsConfig } from "@src/appV2/FeatureFlags/CbhFeatureFlags";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

function buildStorageKey(workerId: string): string {
  return `ec-education-seen::${workerId}`;
}

export function hasWorkerSeenEmergencyCreditsEducation(workerId: string): boolean {
  const seenInLocalStorage = localStorage.getItem(buildStorageKey(workerId));
  return isDefined(seenInLocalStorage);
}

export function markWorkerAsSeenEmergencyCreditsEducation(workerId: string): void {
  localStorage.setItem(buildStorageKey(workerId), "true");
}

export type EnabledEmergencyCreditsConfig = Extract<EmergencyCreditsConfig, { enabled: true }>;

interface UseEmergencyCreditsEducationResult {
  shouldShowEmergencyCreditsEducation: boolean;
  isEmergencyCreditsEnabled: boolean;
  hasSeenEmergencyCreditsEducation: boolean;
  markEmergencyCreditsEducationAsSeen: () => void;
  emergencyCreditsConfig: EnabledEmergencyCreditsConfig | undefined;
}

export function useEmergencyCreditsEducation(): UseEmergencyCreditsEducationResult {
  const { userId: workerId } = useDefinedWorker();

  const emergencyCreditsConfig = useCbhFlag(CbhFeatureFlag.EMERGENCY_CREDITS_CONFIG, {
    defaultValue: { enabled: false },
  });

  const isEmergencyCreditsEnabled = emergencyCreditsConfig.enabled;
  const hasSeenEmergencyCreditsEducation = hasWorkerSeenEmergencyCreditsEducation(workerId);

  const shouldShowEmergencyCreditsEducation =
    isEmergencyCreditsEnabled && !hasSeenEmergencyCreditsEducation;

  return {
    isEmergencyCreditsEnabled,
    hasSeenEmergencyCreditsEducation,
    shouldShowEmergencyCreditsEducation,
    markEmergencyCreditsEducationAsSeen: () => {
      markWorkerAsSeenEmergencyCreditsEducation(workerId);
    },
    emergencyCreditsConfig: emergencyCreditsConfig.enabled ? emergencyCreditsConfig : undefined,
  };
}
